import React, { useEffect, useState } from "react";
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, Button, } from "@mui/material";
import StackedBarChartStatus from "../../components/StackedBarChart/StackedBarChartStatus";
import CustomPieChart from "../DonutChart/DonutChart";
import { SparkLineChart } from '@mui/x-charts/SparkLineChart';
import { areaElementClasses, lineElementClasses } from '@mui/x-charts/LineChart';
import { chartsAxisHighlightClasses } from '@mui/x-charts/ChartsAxisHighlight';
import TrendOvertimeLineChart from '../../components/LineChart/TrendOverTime';
import StpTrendsLineChart from '../../components/LineChart/StpTrendsLineChart';
import AgingTable from '../../components/AgingTable/AgingTable';
import StackedBarChartbyDynamicLegends from '../../components/StackedBarChart/StackedBarChartbyDynamicLegends';
import StackedBarChart from '../../components/StackedBarChart/StackedBarChart';
import { CircularProgress } from "@mui/material";
interface InvoiceFilters {
  invoice_status: string[];
  invoice_type: string[];
  rejection_type: string[];
  rejection_reason: string[];
}

const capitalizeWords = (value: string) => {
  if (!value) return "";

  const acronyms = ["PO", "BU", "ERP"]; // Add more acronyms here as needed

  return value
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => {
      const upperWord = word.toUpperCase();
      if (acronyms.includes(upperWord)) {
        return upperWord;
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
};

const columns = [
  {
    field: 'invoiceid',
    headerName: 'Invoice ID',
    flex: 1,
    align: 'left',
    headerAlign: 'left'
  },
  {
    field: 'invoicenumber',
    headerName: 'Invoice number',
    flex: 1,
    align: 'left',
    headerAlign: 'left'
  },
  {
    field: 'supplier',
    headerName: 'Supplier name',
    flex: 1,
    align: 'left',
    headerAlign: 'left',
    valueFormatter: ({ value }: { value: string }) => capitalizeWords(value)
  },
  {
    field: 'businessunit',
    headerName: 'Business unit',
    flex: 1,
    align: 'left',
    headerAlign: 'left',
    valueFormatter: ({ value }: { value: string }) => capitalizeWords(value)
  },
  {
    field: 'status',
    headerName: 'Status',
    flex: 1,
    align: 'left',
    headerAlign: 'left',
    valueFormatter: ({ value }: { value: string }) => capitalizeWords(value)
  },
  {
    field: 'age',
    headerName: 'Age (in days)',
    type: 'number',
    flex: 1,
    align: 'left',
    headerAlign: 'left'
  },
] as const;

const INVOICE_STATUS_OPTIONS = [
  "Rejected", "Pending", "Processed", "Created", "Processing"
];
const INVOICE_TYPE_OPTIONS = [
  "Credit Note", "Debit Note", "Invoice"
];
const REJECTION_TYPE_OPTIONS = [
  "automatic", "manual"
];
const REJECTION_REASON_OPTIONS = [
  "Duplicate", "Incorrect Amount", "Missing Info"
];

export default function InvoiceOperationsFilters({
  data,
  isPending,
  onApplyInvOpsRptFilter,
  onClear,
  initialFilters = {},
  invoiceStatusListData,
  invoiceRejectionListData,
  invoiceTypeListData
}: {
  data: any,
  isPending: boolean,
  onApplyInvOpsRptFilter: (invOpsRptfilters: InvoiceFilters) => void;
  onClear?: (tabIndex: number) => void;
  initialFilters?: Partial<InvoiceFilters>;
  invoiceStatusListData: any;
  invoiceRejectionListData: any;
  invoiceTypeListData: any;
}) {
  const [dataByStatus, setDataByStatus] = useState([]);
  const [dataByBU, setDataByBU] = useState([]);
  const [pendingInv, setPendingInv] = useState([]);
  const [processingInvArr, setProcessingArr] = React.useState([]);
  const [reviewInvArr, setReviewArr] = React.useState([]);
  const [validationInvArr, setValidationArr] = React.useState([]);
  const [invoicesIngByStatus, setInvoicesIng] = useState([]);
  const [trendOvertime, setTrendOvertime] = useState([]);
  const [touchlessStpTrends, setTouchlessSTPTrends] = useState([]);
  const [manualRevOutcome, setManualRevOutcome] = useState([]);
  const [invOperationAgingException, setInvOperationAgingException] = useState([])
  const [automaticManualRejReason, setAutomaticManualRejReason] = useState([]);
  const [automaticRejections, setAutomaticRejections] = useState([]);
  const [manualRejections, setManualRejections] = useState([]);
  const [invOpsRptfilters, setInvOpsRptfilters] = useState<InvoiceFilters>({
    invoice_status: initialFilters?.invoice_status || [],
    invoice_type: initialFilters?.invoice_type || [],
    rejection_type: initialFilters?.rejection_type || [],
    rejection_reason: initialFilters?.rejection_reason || []
  });
  const [manualReviewReasons, setManualReviewReasons] = useState([]);
  const [manualReviewRejectionReasons, setManualReviewRejectionReasons] = useState([]);
  const returnTotalCount = ((arr) => {
    return arr.reduce((acc, val) => acc + val, 0);
  });
  const transformInvoices = (data) => {
    return data.map(item => {
      // Replace underscores with spaces and split into words
      let words = item.invoicestatus.replace(/_/g, ' ').split(' ');

      // Capitalize first letter of each word
      words = words.map(word =>
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      );

      // Join back into a string
      let label = words.join(' ');

      // Replace all case-insensitive 'Erp' with 'ERP'
      label = label.replace(/Erp/gi, 'ERP');

      return {
        value: item.count,
        label
      };
    });
  };

  const coloursByStatusDonut = ['#F0EDFA', '#F0EDFA',
    '#D1CAEB', '#B9AFDB', '#A296CC', '#9384C7',
    '#7B6AB8', '#6754AD'
  ];
  const coloursByOutcome = ['#B9AFDB', '#543EA3'];
  const transformOutcomes = (data) => {
    return data.map(item => {
      // Replace underscores with spaces and split into words
      let words = item.status.replace(/_/g, ' ').split(' ');

      // Capitalize first letter of each word
      words = words.map(word =>
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      );

      // Join back into a string
      let label = words.join(' ');

      // Replace all case-insensitive 'Erp' with 'ERP'
      label = label.replace(/Erp/gi, 'ERP');

      return {
        value: item.count,
        label
      };
    });
  };


  useEffect(() => {
    setInvOpsRptfilters({
      invoice_status: initialFilters?.invoice_status || [],
      invoice_type: initialFilters?.invoice_type || [],
      rejection_type: initialFilters?.rejection_type || [],
      rejection_reason: initialFilters?.rejection_reason || []
    });
  }, [initialFilters]);

  useEffect(() => {
    if (data?.daily_invoices_by_status?.length) {
      setDataByStatus(data?.daily_invoices_by_status);

    } else {
      setDataByStatus([]);
    }
  }, [data?.daily_invoices_by_status])

  useEffect(() => {
    if (data?.aging_exception_invoice_list?.length) {
      setInvOperationAgingException(data?.aging_exception_invoice_list);
    } else {
      setInvOperationAgingException([]);
    }
  }, [data?.aging_exception_invoice_list])

  useEffect(() => {
    if (data?.pending_invoices?.length) {
      setPendingInv(data?.pending_invoices);
      const processingArr = data?.pending_invoices.map((arr) => {
        return arr.processing;
      });
      const reviewArr = data?.pending_invoices.map((arr) => {
        return arr.review;
      });
      const validationERP = data?.pending_invoices.map((arr) => {
        return arr.validation_in_erp;
      });
      setProcessingArr(processingArr);
      setReviewArr(reviewArr);
      setValidationArr(validationERP);
    }
  }, [data?.pending_invoices])

  useEffect(() => {
    if (data?.invoices_by_status?.length) {
      const transformedData = transformInvoices(data?.invoices_by_status);
      setInvoicesIng(transformedData);
    }
  }, [data?.invoices_by_status])

  useEffect(() => {
    if (data?.trend_over_time?.length) {
      setTrendOvertime(data?.trend_over_time);
    } else {
      setTrendOvertime([]);
    }
  }, [data?.trend_over_time])

  useEffect(() => {
    if (data?.touchless_stp_trends?.length) {
      setTouchlessSTPTrends(data?.touchless_stp_trends);
    }
  }, [data?.touchless_stp_trends])

  useEffect(() => {
    if (data?.manual_review_outcomes?.length) {


      const transformedData = transformOutcomes(data?.manual_review_outcomes);
      setManualRevOutcome(transformedData);
    } else {
      setManualRevOutcome([]);
    }
    if (data?.daily_invoices_by_business_unit) {
      setDataByBU(data.daily_invoices_by_business_unit)
    }
  }, [data?.manual_review_outcomes, data?.daily_invoices_by_business_unit])

  useEffect(() => {
    if (data?.rejection_reasons) {
      setAutomaticManualRejReason(data?.rejection_reasons?.automatic_vs_manual);
      setAutomaticRejections(data?.rejection_reasons?.automatic_rejections);
      setManualRejections(data?.rejection_reasons?.manual_rejections);
    }
  }, [data?.rejection_reasons])

  useEffect(() => {
    if (data?.manual_review_reasons?.length) {
      setManualReviewReasons(data?.manual_review_reasons)
    } else {
      setManualReviewReasons([])
    }
    if (data?.manual_review_rejection_reasons?.length) {
      setManualReviewRejectionReasons(data?.manual_review_rejection_reasons)
    } else {
      setManualReviewRejectionReasons([])
    }
  }, [data])


  const handleChange = (field, event) => {
    setInvOpsRptfilters((prev) => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleApply = () => {
    onApplyInvOpsRptFilter(invOpsRptfilters);
  }
  const handleClear = () => {
    setInvOpsRptfilters({
      invoice_status: [],
      invoice_type: [],
      rejection_type: [],
      rejection_reason: []
    });
    onClear && onClear(0);
  };

  const series = [
    {
      dataKey: "count",
      label: "Invoices",
      color: "#543EA3"
    }
  ];

  return (
    <>
      {
        isPending ? <Box sx={{ display: 'flex', justifyContent: 'center' }}><CircularProgress sx={{ margin: '0 auto', mt: "2%" }} /></Box> : <>
          <Box sx={{ mt: 0, display: "flex", gap: 3, alignItems: 'flex-end', marginBottom: '20px' }}>
            <Box sx={{ display: 'flex', gap: "8px", justifyContent: 'center', alignItems: 'start', flexDirection: "column" }}>
              <InputLabel sx={{ fontSize: '12px', fontWeight: '400' }}>Invoice Status</InputLabel>
              <FormControl fullWidth sx={{ height: '40px' }} size="small">
                <Select
                  multiple
                  value={invOpsRptfilters.invoice_status}
                  onChange={event => handleChange("invoice_status", event)}
                  displayEmpty
                  sx={{ background: 'var(--white)', height: '38px', minWidth: '185px', maxWidth: '185px', borderRadius: '6' }}
                  renderValue={selected => selected.length ? selected.map(opt => opt.replaceAll("_", " ")).join(", ") : <span style={{ color: 'var(--brand-text-default)', fontSize: '14px' }}>Select status</span>}
                >
                  {/* <MenuItem value=""><em>All</em></MenuItem> */}
                  {invoiceStatusListData.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt.replaceAll("_", " ").replace(/\bErp\b/g, "ERP")}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: "8px", justifyContent: 'center', alignItems: 'start', flexDirection: "column" }}>
              <InputLabel sx={{ fontSize: '12px', fontWeight: '400' }}>Invoice type (Invoice, Credit Note, etc.)</InputLabel>
              <FormControl fullWidth sx={{ height: '40px' }} size="small">
                <Select
                  multiple
                  name="Invoice Type"
                  value={invOpsRptfilters.invoice_type}
                  onChange={event => handleChange("invoice_type", event)}
                  displayEmpty
                  renderValue={selected => selected.length ? selected.map(opt => opt.replaceAll("_", " ")).join(",") : <span style={{ color: 'var(--brand-text-default)', fontSize: '14px' }}>Select invoice type</span>}
                  sx={{ background: 'var(--white)', height: '38px', minWidth: '210px', maxWidth: '210px', borderRadius: '6' }}
                >
                  {/* <MenuItem value=""><em>All</em></MenuItem> */}
                  {invoiceTypeListData.map(opt => opt && (
                    <MenuItem key={opt} value={opt}>{opt.replaceAll("_", " ")}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', gap: "8px", justifyContent: 'center', alignItems: 'start', flexDirection: "column" }}>
              <InputLabel sx={{ fontSize: '12px', fontWeight: '400' }}>Rejection type (automatic/manual)</InputLabel>
              <FormControl fullWidth sx={{ height: '40px' }} size="small">
                <Select
                  multiple
                  name="Rejection Type"
                  value={invOpsRptfilters.rejection_type}
                  displayEmpty
                  renderValue={selected => selected.length ? selected.map(opt => opt.charAt(0).toUpperCase() + opt.slice(1)).join(",") : <span style={{ color: 'var(--brand-text-default)', fontSize: '14px' }}>Select rejection type</span>}
                  onChange={event => handleChange("rejection_type", event)}
                  sx={{ background: 'var(--white)', height: '38px', minWidth: '195px', maxWidth: '195px', borderRadius: '6' }}
                >
                  {/* <MenuItem value=""><em>All</em></MenuItem> */}
                  {REJECTION_TYPE_OPTIONS.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: "8px", justifyContent: 'center', alignItems: 'start', flexDirection: "column" }}>
              <InputLabel sx={{ fontSize: '12px', fontWeight: '400' }}>Rejection reason</InputLabel>
              <FormControl fullWidth sx={{ height: '40px' }} size="small">
                <Select
                  multiple
                  name="Rejection Reason"
                  displayEmpty
                  renderValue={selected => selected.length ? selected.join(",") : <span style={{ color: 'var(--brand-text-default)', fontSize: '14px' }}>Select rejection reason</span>}
                  value={invOpsRptfilters.rejection_reason}
                  onChange={event => handleChange("rejection_reason", event)}
                  sx={{ background: 'var(--white)', height: '38px', minWidth: '195px', maxWidth: '195px', borderRadius: '6' }}
                >
                  {/* <MenuItem value=""><em>All</em></MenuItem> */}
                  {invoiceRejectionListData.map(opt => (
                    opt && <MenuItem key={opt} value={opt}>{opt.replace(/_/g, " ")}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 2, mb: '2px' }}>
              <Button variant="contained" color="primary" onClick={handleApply} sx={{ minWidth: '102px', fontSize: '14px', padding: '10px 12px', fontWeight: 600, lineHeight: '18px', height: 'auto' }}>
                Apply filters
              </Button>
              <Button variant="outlined" color="secondary" onClick={handleClear} sx={{ fontSize: '14px', fontWeight: 600 }}>
                Reset
              </Button>
            </Box>
          </Box>
          <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
            <StackedBarChartStatus data={dataByStatus} title='Daily invoices ingestion (by status)' xAxisLabel="Date" />
            {dataByBU?.length && <StackedBarChartbyDynamicLegends
              data={dataByBU}
              title="Daily invoices ingestion (by business units)"
              yAxisLabel="Invoices"
              xAxisLabel="Date"
              xAxisDataKey="date"
              valueFormatterReq={true}
            />}
            <Box sx={{ width: "100%", display: "flex", gap: "32px" }}>
              <CustomPieChart data={invoicesIngByStatus} title="Invoice distribution by status" colours={coloursByStatusDonut} />
              <Box sx={{ width: "65%", boxSizing: "border-box", padding: "24px", border: '1px solid #E5E5E5', borderRadius: '8px', backgroundColor: 'var(--white)' }}>

                <Typography variant="h6" component="h4" sx={{ mb: 4, fontWeight: 700, fontSize: '20px' }}>
                  Invoice pending by status
                </Typography>
                <Box style={{ display: 'flex', flexDirection: 'row' }}>
                  <Box sx={{ mr: 1 }}>
                    <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 700 }}>For Processing</Typography>
                    <Typography variant="h6" sx={{ fontSize: 32, fontWeight: 'bold', mb: 1 }}>{returnTotalCount(processingInvArr)}</Typography>
                    <SparkLineChart data={processingInvArr}
                      height={100}
                      width={209}
                      showHighlight={true}
                      showTooltip={true}
                      area
                      color="#543EA3"
                      sx={{
                        [`& .${areaElementClasses.root}`]: { opacity: 0.1 },
                        [`& .${lineElementClasses.root}`]: { strokeWidth: 2 },
                        [`& .${chartsAxisHighlightClasses.root}`]: {
                          stroke: '#543EA3',
                          strokeDasharray: 'none',
                          strokeWidth: 2,
                        },
                      }}
                    />
                  </Box>
                  <Box sx={{ mr: 1 }}>
                    <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 700 }}>For Review</Typography>
                    <Typography variant="h6" sx={{ fontSize: 32, fontWeight: 'bold', mb: 1 }}>{returnTotalCount(reviewInvArr)}</Typography>
                    <SparkLineChart data={reviewInvArr}
                      height={100}
                      width={209}
                      showHighlight={true}
                      showTooltip={true}
                      area
                      color="#543EA3"
                      sx={{
                        [`& .${areaElementClasses.root}`]: { opacity: 0.1 },
                        [`& .${lineElementClasses.root}`]: { strokeWidth: 2 },
                        [`& .${chartsAxisHighlightClasses.root}`]: {
                          stroke: '#543EA3',
                          strokeDasharray: 'none',
                          strokeWidth: 2,
                        },
                      }}
                    />
                  </Box>
                  <Box sx={{ backgroundColor: 'var(--white)' }}>
                    <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 700 }}>For Validation in ERP</Typography>
                    <Typography variant="h6" sx={{ fontSize: 32, fontWeight: 'bold', mb: 1 }}>{returnTotalCount(validationInvArr)}</Typography>
                    <SparkLineChart data={validationInvArr}
                      height={100}
                      width={209}
                      showHighlight={true}
                      showTooltip={true}
                      area
                      color="#543EA3"
                      sx={{
                        [`& .${areaElementClasses.root}`]: { opacity: 0.1 },
                        [`& .${lineElementClasses.root}`]: { strokeWidth: 2 },
                        [`& .${chartsAxisHighlightClasses.root}`]: {
                          stroke: '#543EA3',
                          strokeDasharray: 'none',
                          strokeWidth: 2,
                        },
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            <TrendOvertimeLineChart dataset={trendOvertime} title="Invoices processed per day/week" xAxisLabel="Date" />

            <StackedBarChartbyDynamicLegends
              data={automaticManualRejReason}
              title="Rejection Type: automatic vs. manual"
              yAxisLabel="Invoices"
              xAxisDataKey="date"
              xAxisLabel="Date"
              valueFormatterReq={true}
            />

            <StackedBarChartbyDynamicLegends
              data={automaticRejections}
              title="Automatic rejection reasons"
              yAxisLabel="Invoices"
              xAxisDataKey="date"
              xAxisLabel="Date"
              valueFormatterReq={true}
            />

            <StackedBarChartbyDynamicLegends
              data={manualRejections}
              title="Manual rejection reasons"
              yAxisLabel="Invoices"
              xAxisDataKey="date"
              xAxisLabel="Date"
              valueFormatterReq={true}
            />

            <Box sx={{ display: 'flex', gap: '32px', width: '100%' }}>
              <CustomPieChart data={manualRevOutcome} title="Manual review outcomes (passed vs. rejected)" colours={coloursByOutcome} />
              <StackedBarChart
                data={manualReviewReasons}
                series={series}
                xAxisKey="manualreviewreason"
                xAxisLabel="Reasons for manual review"
                yAxisLabel="Invoices"
                title="Reasons for manual review (STP failure reasons)"
                sx={{ width: '65%' }}
              />
            </Box>
            <StackedBarChart
              data={manualReviewRejectionReasons}
              series={series}
              xAxisKey="status"
              xAxisLabel="Reasons for manual review rejection"
              yAxisLabel="Invoices"
              title="Reasons for manual review rejection"
            />
            <StpTrendsLineChart dataset={touchlessStpTrends} title="Touchless & STP trends" xAxisLabel="Date" />
            <AgingTable rows={invOperationAgingException?.length ? invOperationAgingException : []} loading={isPending} columns={columns} title={"Aging exception list"} />
          </div>
        </>
      }
    </>
  )
}
